import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Package, MapPin, Calendar, Star, Plus, Edit, MessageCircle } from "lucide-react";
import { ProductCardCompact } from "@/components/ProductCards";
import api from "@/utils/api";

const ProfilePage = ({ currentUser, userCurrency }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, listingsRes] = await Promise.all([
          api.get(`/users/${userId}/profile`),
          api.get(`/listings?seller=${userId}&limit=12`)
        ]);
        setProfile(profileRes.data);
        const listingsData = Array.isArray(listingsRes.data) 
          ? listingsRes.data 
          : (listingsRes.data?.listings || []);
        setListings(listingsData);
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('User not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen pt-52 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!profile) return null;

  const memberSince = profile.created_at 
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Unknown';

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50" data-testid="profile-page">
      <div className="container mx-auto px-4 md:px-6">
        {/* Profile Header */}
        <Card className="mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-24" />
          <div className="px-6 pb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-12">
              <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                {profile.picture ? (
                  <AvatarImage src={profile.picture} alt={profile.name} />
                ) : (
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl">
                    {profile.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1">
                <h1 className="text-2xl font-bold" data-testid="profile-name">{profile.name}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Member since {memberSince}
                  </span>
                  {profile.address && (profile.address.city || profile.address.state) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {[profile.address.city, profile.address.state, profile.address.country].filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
                {/* Rating */}
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(profile.avg_rating || 0)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-slate-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{profile.avg_rating || 0}</span>
                  <span className="text-sm text-muted-foreground">({profile.review_count || 0} reviews)</span>
                </div>
              </div>
              {isOwnProfile && (
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  <Edit className="h-4 w-4 mr-2" /> Edit Profile
                </Button>
              )}
              {!isOwnProfile && currentUser && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (listings.length > 0) {
                      navigate(`/item/${listings[0].listing_id}`);
                    }
                  }}
                >
                  <MessageCircle className="h-4 w-4 mr-2" /> Message
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{profile.listing_count || 0}</p>
            <p className="text-sm text-muted-foreground">Active Listings</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{profile.sales_count || 0}</p>
            <p className="text-sm text-muted-foreground">Items Sold</p>
          </Card>
          <Card className="p-4 text-center">
            <div className="flex items-center justify-center gap-1">
              <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
              <span className="text-2xl font-bold text-emerald-600">{profile.avg_rating || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Rating</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{profile.review_count || 0}</p>
            <p className="text-sm text-muted-foreground">Reviews</p>
          </Card>
        </div>

        {/* Active Listings */}
        <div>
          <h2 className="text-xl font-semibold mb-4">{isOwnProfile ? 'Your' : `${profile.name}'s`} Active Listings</h2>
          {listings.length === 0 ? (
            <Card className="p-12 text-center">
              <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold mb-2">No Active Listings</h3>
              <p className="text-muted-foreground mb-4">
                {isOwnProfile ? "You don't have any active listings" : "This seller has no active listings"}
              </p>
              {isOwnProfile && (
                <Button onClick={() => navigate('/sell')} className="bg-emerald-500 hover:bg-emerald-600">
                  <Plus className="h-4 w-4 mr-2" /> Create Listing
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {listings.map((listing) => (
                <ProductCardCompact
                  key={listing.listing_id}
                  listing={listing}
                  userCurrency={userCurrency}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
